import React, { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { nb, enGB } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface EuroDatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
  placeholder?: string;
}

export function EuroDatePicker({
  value,
  onChange,
  min,
  max,
  className,
  required,
  disabled,
  placeholder,
  ...rest
}: EuroDatePickerProps) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  const ph = placeholder ?? t('euroDatePicker.placeholder', 'dd.mm.åååå');
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(new Date());
  const testId = rest['data-testid'];

  const selectedDate = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const validSelected = selectedDate && isValid(selectedDate) ? selectedDate : undefined;

  const displayText = validSelected
    ? format(validSelected, 'dd.MM.yyyy')
    : '';

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setViewMonth(validSelected || new Date());
    }
    setOpen(isOpen);
  };

  const handleSelect = (date: Date | undefined) => {
    if (date && onChange) {
      onChange(format(date, 'yyyy-MM-dd'));
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !displayText && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 opacity-60 flex-shrink-0" />
          <span className="flex-1 text-left truncate">
            {displayText || ph}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={validSelected}
          onSelect={handleSelect}
          month={viewMonth}
          onMonthChange={setViewMonth}
          disabled={(date: Date) => {
            if (min) {
              const minD = new Date(min + 'T00:00:00');
              if (date < minD) return true;
            }
            if (max) {
              const maxD = new Date(max + 'T23:59:59');
              if (date > maxD) return true;
            }
            return false;
          }}
          locale={isEn ? enGB : nb}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
